// Main App Component
import { useEffect, useRef, lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { MainLayout } from './components/layout/MainLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer } from './components/ui/toast';
import { useToast } from './components/ui/use-toast';
import { Loading } from './components/ui/loading';
import { setToastInstance } from './lib/toastHelper';
import { useTheme } from './lib/useTheme';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import WorkflowsPage from './pages/WorkflowsPage';

// Lazy loading para paginas secundarias
const WorkflowEditorPage = lazy(() => import('./pages/WorkflowEditorPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const DownloadPage = lazy(() => import('./pages/DownloadPage'));
const Diagnostic = lazy(() => import('./pages/Diagnostic'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));

// Paginas de admin (lazy loading)
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminWorkflows = lazy(() => import('./pages/admin/AdminWorkflows'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminExecutionHistory = lazy(() => import('./pages/admin/AdminExecutionHistory'));

function ToastProvider() {
  const { toasts, removeToast, success, error, info, warning } = useToast();

  // RC-02: Usar refs estables para evitar re-registrar el singleton en cada render.
  // Las funciones del hook pueden cambiar de referencia en cada render;
  // los refs aseguran que el singleton siempre llame a la versión más reciente.
  const successRef = useRef(success);
  const errorRef = useRef(error);
  const infoRef = useRef(info);
  const warningRef = useRef(warning);

  // Sincronizar refs con las funciones más recientes del hook (dentro de un efecto
  // para satisfacer react-hooks/refs; las llamadas a toast son siempre asíncronas
  // desde interacciones de usuario, así que los refs siempre estarán actualizados).
  useEffect(() => {
    successRef.current = success;
    errorRef.current = error;
    infoRef.current = info;
    warningRef.current = warning;
  });

  useEffect(() => {
    setToastInstance({
      success: (msg: string, dur?: number) => successRef.current(msg, dur),
      error: (msg: string, dur?: number) => errorRef.current(msg, dur),
      info: (msg: string, dur?: number) => infoRef.current(msg, dur),
      warning: (msg: string, dur?: number) => warningRef.current(msg, dur),
    });
  }, []);

  return <ToastContainer toasts={toasts} onClose={removeToast} />;
}

const LazyFallback = (
  <div className="flex items-center justify-center h-screen">
    <Loading size="lg" />
  </div>
);

// Layout raíz que envuelve todas las rutas (ToastProvider + Suspense + estilos globales)
function RootLayout() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 dark:text-gray-100">
      <ToastProvider />
      <Suspense fallback={LazyFallback}>
        <Outlet />
      </Suspense>
    </div>
  );
}

// createBrowserRouter es un "data router" que habilita useBlocker, useFetcher, etc.
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* Rutas públicas */}
      <Route index element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      {/* P-05: Catch-all para rutas públicas no existentes (ej: /login/extra)
          que no matchean /login ni /register. Sin esto, React Router no
          renderiza nada y el usuario ve pantalla en blanco. */}

      {/* Rutas protegidas */}
      {/* NAV-03: AgentStatusBanner movido dentro de MainLayout para evitar
          empujar el layout completo hacia abajo y posibles solapamientos */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="workflows" element={<WorkflowsPage />} />
        <Route path="workflows/new" element={<WorkflowEditorPage />} />
        <Route path="workflows/:id" element={<WorkflowEditorPage />} />
        <Route path="guide" element={<GuidePage />} />
        <Route path="download" element={<DownloadPage />} />
        <Route path="diagnostic" element={<Diagnostic />} />
        <Route path="settings" element={<SettingsPage />} />

        {/* Rutas de administracion */}
        <Route path="admin" element={<AdminRoute><Outlet /></AdminRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="workflows" element={<AdminWorkflows />} />
          <Route path="workflows/:id" element={<WorkflowEditorPage />} />
          <Route path="history" element={<AdminExecutionHistory />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Route>
  )
);

function App() {
  useTheme();

  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

export default App;
